import { apiClient } from './index';
import { apiBase } from '../../components/mcpSetup';

export interface MonitoringConnection {
  organization_slug?: string;
  has_api_token?: boolean;
  routes?: MonitoringProjectRoute[];
  id: string;
  project_id: string;
  project_name?: string;
  created_by: string;
  provider: 'sentry' | 'generic';
  name: string;
  installation_id: string;
  external_project_id: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  intake_status?: 'ready' | 'paused' | 'unavailable';
  last_received_at?: string;
}

export interface MonitoringIssue {
  project_id?: string;
  project_name?: string;
  route_status?: 'ready' | 'unavailable';
  task_id?: string;
  task_state?: 'unlinked' | 'linked' | 'unavailable';
  id: string;
  connection_id: string;
  context: {
    issue_id: string;
    project_id: string;
    short_id?: string;
    title: string;
    level: string;
    action: string;
    source_url?: string;
    observed_at: string;
  };
  first_received_at: string;
  last_received_at: string;
  deliveries: number;
}

export interface CreateMonitoringConnection {
  provider?: MonitoringConnection['provider'];
  request_id?: string;
  organization_slug?: string;
  api_token?: string;
  routes?: MonitoringRouteInput[];
  project_id?: string;
  name: string;
  installation_id?: string;
  external_project_id?: string;
  client_secret: string;
}

export async function listMonitoringConnections(): Promise<MonitoringConnection[]> {
  const { data } = await apiClient.get<{ data: MonitoringConnection[] }>('/monitoring-connections');
  return data.data ?? [];
}

export async function createMonitoringConnection(input: CreateMonitoringConnection): Promise<MonitoringConnection> {
  const { data } = await apiClient.post<{ data: MonitoringConnection }>('/monitoring-connections', input);
  return data.data;
}

export async function setMonitoringEnabled(id: string, enabled: boolean): Promise<void> {
  await apiClient.patch(`/monitoring-connections/${encodeURIComponent(id)}`, { enabled });
}

export async function deleteMonitoringConnection(id: string): Promise<void> {
  await apiClient.delete(`/monitoring-connections/${encodeURIComponent(id)}`);
}

export async function listMonitoringIssues(id: string, offset = 0): Promise<MonitoringIssue[]> {
  const { data } = await apiClient.get<{ data: MonitoringIssue[] }>(`/monitoring-connections/${encodeURIComponent(id)}/issues`, {
    params: { limit: 25, offset },
  });
  return data.data ?? [];
}

export function monitoringWebhookURL(id: string, provider: MonitoringConnection['provider'] = 'sentry'): string {
  return `${apiBase()}/webhooks/${provider}/${encodeURIComponent(id)}`;
}

// Defense in depth: provider text is untrusted even when its delivery is signed.
export function sentrySourceURL(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.port ||
      !(url.hostname === 'sentry.io' || url.hostname.endsWith('.sentry.io'))) return undefined;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch { return undefined; }
}

export interface ConvertMonitoringIssue {
  title: string;
  priority: 'none' | 'low' | 'medium' | 'high' | 'urgent';
}

export async function convertMonitoringIssue(connectionID: string, issueID: string, input: ConvertMonitoringIssue): Promise<{ task_id: string; created: boolean }> {
  const { data } = await apiClient.post<{ data: { task_id: string; created: boolean } }>(
    `/monitoring-connections/${encodeURIComponent(connectionID)}/issues/${encodeURIComponent(issueID)}/task`, input,
  );
  return data.data;
}

export interface MonitoringRouteInput {
  external_project_id: string;
  external_project_name?: string;
  project_id: string;
}
export interface MonitoringProjectRoute extends MonitoringRouteInput {
  project_name: string;
  status: 'ready' | 'unavailable';
}
export interface SentryProject { id: string; name: string; slug: string }
export interface SentryProjectPage { data: SentryProject[]; next_cursor?: string }
export interface SentryDiscoveryInput { organization_slug: string; api_token: string; query?: string; cursor?: string }

export async function discoverSentryProjects(input: SentryDiscoveryInput): Promise<SentryProjectPage> {
  const { data } = await apiClient.post<SentryProjectPage>('/monitoring-connections/sentry-projects', input);
  return data;
}
export async function listSentryProjects(id: string, query = '', cursor = ''): Promise<SentryProjectPage> {
  const { data } = await apiClient.get<SentryProjectPage>(`/monitoring-connections/${encodeURIComponent(id)}/sentry-projects`, { params: { query, cursor } });
  return data;
}
export async function saveSentryDiscovery(id: string, input: SentryDiscoveryInput): Promise<void> {
  await apiClient.put(`/monitoring-connections/${encodeURIComponent(id)}/discovery`, input);
}
export async function saveMonitoringRoute(id: string, input: MonitoringRouteInput): Promise<void> {
  await apiClient.put(`/monitoring-connections/${encodeURIComponent(id)}/routes`, input);
}
export async function deleteMonitoringRoute(id: string, sourceID: string): Promise<void> {
  await apiClient.delete(`/monitoring-connections/${encodeURIComponent(id)}/routes/${encodeURIComponent(sourceID)}`);
}
export function sentryDiscoveryError(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 422) return 'Check the organization slug and API token. Use a token for this integration with Organization read permission.';
  if (status === 429) return 'Sentry is limiting requests. Wait a minute, then retry.';
  if (status === 401 || status === 403) return 'Your access has changed. Sign in as a workspace admin with permission to manage projects.';
  if (status === 404 || status === 402) return 'The connection, owner, or destination is unavailable. Check access and workspace plan limits.';
  if (status === 400) return 'Check the organization, project selections and token, then retry.';
  return 'Sentry projects could not be loaded or saved. Check your connection and try again.';
}

export interface MonitoringRuleInput {
  enabled: boolean; minimum_level: 'debug' | 'info' | 'warning' | 'error' | 'fatal';
  environment: string; priority: ConvertMonitoringIssue['priority']; daily_limit: number;
}
export interface MonitoringRule extends MonitoringRuleInput {
  id: string; external_project_id: string; created_by: string; revision: number; updated_at: string;
}
export interface MonitoringDecision {
  id: string; issue_id: string; external_project_id: string; state: 'pending' | 'created' | 'linked' | 'inbox_only' | 'skipped' | 'blocked' | 'failed';
  reason: string; task_id?: string; attempts: number; created_at: string; updated_at: string;
}
export async function listMonitoringRules(id: string): Promise<MonitoringRule[]> {
  return (await apiClient.get<{ data: MonitoringRule[] }>(`/monitoring-connections/${encodeURIComponent(id)}/rules`)).data.data;
}
export async function saveMonitoringRule(id: string, source: string, input: MonitoringRuleInput): Promise<MonitoringRule> {
  return (await apiClient.put<MonitoringRule>(`/monitoring-connections/${encodeURIComponent(id)}/rules/${encodeURIComponent(source)}`, input)).data;
}
export async function listMonitoringDecisions(id: string): Promise<MonitoringDecision[]> {
  return (await apiClient.get<{ data: MonitoringDecision[] }>(`/monitoring-connections/${encodeURIComponent(id)}/decisions`)).data.data;
}
export async function retryMonitoringDecision(id: string, decision: string): Promise<void> {
  await apiClient.post(`/monitoring-connections/${encodeURIComponent(id)}/decisions/${encodeURIComponent(decision)}/retry`);
}

export function genericSourceURL(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return undefined;
    url.search = ''; url.hash = ''; return url.toString();
  } catch { return undefined; }
}
export async function rotateMonitoringSecret(id: string, client_secret: string): Promise<void> {
  await apiClient.put(`/monitoring-connections/${encodeURIComponent(id)}/signing-secret`, { client_secret });
}
export interface MonitoringMetrics {
  connections: number; connections_with_receipt: number; metrics_started_at?: string;
  accepted_deliveries: number; duplicate_deliveries: number; linked_tasks: number;
  automatic_tasks: number; manual_conversions: number; awaiting_triage: number; verification_samples: number;
  median_receipt_to_task_seconds?: number; median_receipt_to_verification_seconds?: number;
}
export async function getMonitoringMetrics(): Promise<MonitoringMetrics> {
  return (await apiClient.get<{ data: MonitoringMetrics }>('/monitoring-connections/metrics')).data.data;
}
